import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import successSound from "../assets/sounds/valider.wav";
import failSound from "../assets/sounds/erreur.mp3";
import Breadcrumbs from '../components/Breadcrumbs';

const correctSound = new Audio(successSound);
const errorSound = new Audio(failSound);

const WordSearchExercise = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const { interfaceLang, courseLang, vocabularyCategory } = useParams();
  const navigate = useNavigate();
  const exerciseType = "mot-melee"
  const [sessionId, setSessionId] = useState(null);
  const [grid, setGrid] = useState([]);
  const [placements, setPlacements] = useState([]); // Placements retournés par le backend
  const [foundWords, setFoundWords] = useState([]);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [isMuted, setIsMuted] = useState(false);
  const [foundCells, setFoundCells] = useState(new Set());
  const cellKey = (r, c) => `${r},${c}`;

  // Pour mettre en vert
  const getCellsBetween = (start, end) => {
    const dr = Math.sign(end.row - start.row);
    const dc = Math.sign(end.col - start.col);

    const cells = [];
    let r = start.row;
    let c = start.col;
    cells.push({ row: r, col: c });

    while (r !== end.row || c !== end.col) {
      r += dr;
      c += dc;
      cells.push({ row: r, col: c });
    }
    return cells;
  };

  // Récupère ou crée la session pour l'exercice (similaire à l'exercice précédent)
  const fetchOrCreateSession = async () => {
    const token = localStorage.getItem('access_token');
    try {
      const response = await axios.get(
        `${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/session/${exerciseType}/`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      console.log(response)
      setSessionId(response.data.id);
    } catch (error) {
      console.error("Erreur lors de la récupération de la session", error);
    }
  };

  // Récupérer la grille et les placements depuis le backend
  const fetchPuzzle = async () => {
    const token = localStorage.getItem('access_token');
    try {
      const response = await axios.get(`${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/wordsearch/?sessionId=${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Si le backend renvoie un message indiquant qu'il n'y a plus de mots, rediriger vers la page de choix
      if(response.data.message === "No words left"){
         navigate(`/${interfaceLang}/courses/${courseLang}/vocabulary/${vocabularyCategory}`);
         return;
      }
      setGrid(response.data.grid);
      setPlacements(response.data.placements);
      setFoundWords([]);
      setFoundCells(new Set());  
      setSelectionStart(null);
      setSelectionEnd(null);
      setFeedback("");
    } catch (error) {
      console.error("Erreur lors de la récupération du puzzle", error);
    }
  };

  useEffect(() => {
    fetchOrCreateSession();
  }, [vocabularyCategory, exerciseType]);

  useEffect(() => {
    if(sessionId) {
      fetchPuzzle();
    }
  }, [sessionId, vocabularyCategory]);

  // Gérer la sélection d'une cellule de la grille
  const handleCellClick = (rowIndex, colIndex) => {
    if (!selectionStart) {
      setSelectionStart({ row: rowIndex, col: colIndex });
    } else if (!selectionEnd) {
      setSelectionEnd({ row: rowIndex, col: colIndex });
      checkSelection(selectionStart, { row: rowIndex, col: colIndex });
    } else {
      setSelectionStart({ row: rowIndex, col: colIndex });
      setSelectionEnd(null);
    }
  };

  // Vérifier si la sélection correspond à un des placements (on autorise la sélection inversée)
  const checkSelection = (start, end) => {
    const matchIndex = placements.findIndex(p => {
      const s = p.start;
      const e = p.end;
      return (
        ((start.row === s.row && start.col === s.col && end.row === e.row && end.col === e.col) ||
         (start.row === e.row && start.col === e.col && end.row === s.row && end.col === s.col))
      );
    });
    if (matchIndex !== -1 && !foundWords.includes(matchIndex)) {
      if (!isMuted) correctSound.play();
      setFeedback("Mot trouvé !");
      setFoundWords(prev => [...prev, matchIndex]);

      // ➜ Ajouter toutes les cellules du mot trouvé dans foundCells
      const { start, end, word } = placements[matchIndex];
      const cells = getCellsBetween(start, end);
      setFoundCells(prev => {
        const next = new Set(prev);
        cells.forEach(({ row, col }) => next.add(cellKey(row, col)));
        return next;
      });
      
      // Valider le mot dans la bdd
      const token = localStorage.getItem('access_token');
      const endpoint = `${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/exercises/validate/`;
      

      axios.post(endpoint, {
          sessionId: sessionId,
          word: word
      }, {
          headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(() => {
          // Après validation, on récupère un nouveau mot
          console.log("mot sauvegardé");
      })
      .catch(error => {
          console.error("Erreur lors de la validation", error);
      });

      // Réinitialiser la sélection
      setSelectionStart(null);
      setSelectionEnd(null);
      // Si tous les mots sont trouvés, charger un nouveau puzzle après un court délai
      if (foundWords.length + 1 === placements.length) {
        setTimeout(() => {
          fetchPuzzle();
        }, 1000);
      }
    } else {
      if (!isMuted) errorSound.play();
      setFeedback("Ce n'est pas un mot valide.");
      setSelectionStart(null);
      setSelectionEnd(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Bouton pour activer/désactiver le son */}
      <button
        className="absolute top-4 right-4 p-2 rounded-full border border-gray-300 hover:bg-gray-100 transition"
        onClick={() => setIsMuted(prev => !prev)}
      >
        {isMuted ? "Activer le son" : "Couper le son"}
      </button>
      <h2 className="text-2xl font-bold text-center mb-4">Exercice de Mot Mêlé</h2>
      {feedback && <p className="text-center text-lg mb-2">{feedback}</p>}
      
      <div className="flex flex-col items-center">
        {/* Affichage de la grille sous forme de tableau */}
        <table className="border-collapse">
          <tbody>
            {grid.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((letter, colIndex) => (
                  <td
                    key={colIndex}
                    className={[
                      "w-8 h-8 border border-gray-300 text-center cursor-pointer select-none text-lg",
                      foundCells.has(cellKey(rowIndex, colIndex))
                        ? "bg-green-300" // ➜ mot trouvé
                        : ((selectionStart && selectionStart.row === rowIndex && selectionStart.col === colIndex) ||
                          (selectionEnd && selectionEnd.row === rowIndex && selectionEnd.col === colIndex))
                          ? "bg-yellow-200" // ➜ points de sélection
                          : "bg-white"
                    ].join(" ")}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                    
                  >
                    {letter}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Liste des mots à trouver */}
        <div className="mt-4">
          <h3 className="text-xl font-semibold">Mots à trouver :</h3>
          <ul>
            {placements.map((p, index) => (
              <li key={index} className={`text-lg ${foundWords.includes(index) ? 'line-through text-green-600' : 'text-gray-800'}`}>
                {p.word} - {p.translation}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WordSearchExercise;
